import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getMetodosEnvio,
  crearMetodoEnvio,
  actualizarMetodoEnvio,
} from "../../../services/admin/metodos_envio";
import ShippingFormModal from "./ShippingFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";

function ShippingMethodsPage() {
  const [metodos, setMetodos] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedMetodo, setSelectedMetodo] = useState(null);

  // ✅ Alert simple
  const [alert, setAlert] = useState(null); // { type, message }
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarMetodos = useCallback(async () => {
    try {
      const data = await getMetodosEnvio();
      setMetodos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading shipping methods:", error);
      showAlert("error", error?.message || "Failed to load shipping methods.");
      setMetodos([]);
    }
  }, [showAlert]);

  useEffect(() => {
    cargarMetodos();
  }, [cargarMetodos]);

  // ✅ Filter search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return metodos;

    return metodos.filter((m) =>
      String(m?.nombre || "").toLowerCase().includes(q)
    );
  }, [metodos, search]);

  // ✅ totalPages basado en filtered (important)
  const totalPages = useMemo(() => {
    return Math.ceil(filtered.length / pageSize) || 1;
  }, [filtered.length, pageSize]);

  // ✅ clamp page si cambia el filtro y quedas en una pagina que ya no existe
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const nextPage = () => page < totalPages && setPage((p) => p + 1);
  const prevPage = () => page > 1 && setPage((p) => p - 1);

  const openCreateModal = () => {
    setSelectedMetodo(null);
    setShowModal(true);
  };

  const openEditModal = (metodo) => {
    setSelectedMetodo(metodo);
    setShowModal(true);
  };

  // ✅ IMPORTANTE:
  // - success se muestra arriba
  // - error se re-throw para que ShippingFormModal lo muestre dentro del modal
  const handleCreate = async (data) => {
    try {
      const res = await crearMetodoEnvio(data);
      showAlert("success", res?.message || "Shipping method created successfully.");
      await cargarMetodos();
      setShowModal(false);
    } catch (error) {
      console.error("Create shipping method failed:", error);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await actualizarMetodoEnvio(id, data);
      showAlert("success", res?.message || "Shipping method updated successfully.");
      await cargarMetodos();
      setShowModal(false);
    } catch (error) {
      console.error("Update shipping method failed:", error);
      throw error;
    }
  };

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {/* ✅ ALERT */}
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      {/* HEADER */}
      <div className="header">
        <h2 className="title">Shipping Methods</h2>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Search shipping method..."
            className="search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          {hasPerm("Create shipping methods") && (
            <button className="btn-create" onClick={openCreateModal}>
              Create Method
            </button>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Method Name</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">
                  No shipping methods found
                </td>
              </tr>
            ) : (
              paginated.map((m) => (
                <tr key={m.metodo_envio_id}>
                  <td>{m.metodo_envio_id}</td>
                  <td>{m.nombre}</td>

                  <td>
                    <div className="actions">
                      {hasPerm("Update shipping methods") && (
                        <span
                          className="action-icon edit"
                          onClick={() => openEditModal(m)}
                          title="Edit shipping method"
                        >
                          {IconEdit}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <ShippingFormModal
          isOpen={showModal}
          metodoData={selectedMetodo}
          onClose={() => setShowModal(false)}
          onSubmit={(data) =>
            selectedMetodo
              ? handleUpdate(selectedMetodo.metodo_envio_id, data)
              : handleCreate(data)
          }
        />
      )}

      {/* PAGINATION */}
      <div className="pagination">
        <button onClick={prevPage} disabled={page === 1}>
          Prev
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button onClick={nextPage} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
}

/* -------------------- SAME MODERN STYLES -------------------- */

const modernStyles = `
.page-container {
  /* Llena el alto de la tarjeta del layout; solo el listado scrollea. */
  height: 100%;
  padding: 4px 0 0;
  font-family: 'Segoe UI';
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
/* Cabecera, alerta y paginacion conservan su alto; no se comprimen. */
.page-container > .header,
.page-container > .pagination,
.page-container > .omx-alert,
.page-container > .lista-recortada {
  flex: none;
}


/* ✅ ALERT */
.omx-alert{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid;
  margin: 0 0 14px 0;
  box-shadow: 0 10px 25px rgba(0,0,0,.08);
}
.omx-alert__msg{ font-size:14px; font-weight:600; }
.omx-alert__x{
  border:none;
  background:transparent;
  cursor:pointer;
  font-size:18px;
  opacity:.7;
}
.omx-alert__x:hover{ opacity:1; }

.omx-alert--success{ background:#ECFDF5; border-color:#A7F3D0; color:#065F46; }
.omx-alert--error{ background:#FEF2F2; border-color:#FECACA; color:#7F1D1D; }
.omx-alert--info{ background:#EFF6FF; border-color:#BFDBFE; color:#1E3A8A; }

.title {
  font-size: 26px;
  font-weight: 700;
  color: #2F4779;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
}

.search-input {
  padding: 10px 16px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E293B;
  font-size: 14px;
  transition: 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3B82F6;
  background: white;
}

.btn-create {
  padding: 10px 18px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.25s;
}

.btn-create:hover {
  background: #2563EB;
}

.table-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);

  /* min-height: 0 permite encogerse por debajo del contenido; sin eso
     el scroll interno no ocurre. */
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
  margin-top: 20px;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #F1F5F9;
  padding: 14px;
  font-size: 13px;
  color: #475569;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td {
  padding: 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
}

.modern-table tr:hover td {
  background: #F8FAFC;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.action-icon {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.action-icon.edit:hover {
  background: #DBEAFE;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #94A3B8;
  font-size: 14px;
}

.pagination {
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 8px 14px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: .5;
}

.pagination span {
  font-size: 14px;
  color: #475569;
}
`;

export default ShippingMethodsPage;
